import { Injectable } from '@angular/core';
import {
  InvoiceFieldError,
  InvoiceValidationContext,
  MultiLangMessagePayload,
} from '../../../models/invoice-v2.model';

type Lang = 'ar' | 'en' | 'he';

@Injectable({ providedIn: 'root' })
export class InvoiceV2ValidationService {
  private readonly paymentDetailLabels: Record<
    string,
    { ar: string; en: string; he: string }
  > = {
    date: { ar: 'تاريخ الدفع', en: 'Payment date', he: 'תאריך תשלום' },
    reference: { ar: 'رقم الشيك', en: 'Check number', he: "מס' שיק" },
    bank: { ar: 'البنك', en: 'Bank', he: 'בנק' },
    branch: { ar: 'الفرع', en: 'Branch', he: 'סניף' },
    account: { ar: 'رقم الحساب', en: 'Account', he: 'חשבון' },
    credit_card_code: { ar: '4 أرقام أخيرة', en: 'Last 4 digits', he: '4 ספרות אחרונות' },
    credit_card_name: { ar: 'اسم البطاقة', en: 'Card name', he: 'שם כרטיס' },
    credit_type: { ar: 'نوع الائتمان', en: 'Credit type', he: 'סוג אשראי' },
    details: { ar: 'تفاصيل الدفع', en: 'Payment details', he: 'פרטי תשלום' },
    account_number: { ar: 'رقم الحساب', en: 'Account number', he: 'מספר חשבון' },
  };

  validate(ctx: InvoiceValidationContext): InvoiceFieldError[] {
    const errors: InvoiceFieldError[] = [];
    const v = ctx.formValue;

    this.validateHeader(v, ctx, errors);
    this.validateBusinessRules(v, ctx, errors);

    if (this.isForeignDocumentCurrency(v)) {
      this.validateForeignCurrency(v, ctx, errors);
    }

    if (ctx.hasItems) {
      this.validateItems(v, ctx, errors);
    }

    if (ctx.hasPayments && ctx.confirm) {
      this.validatePayments(v, ctx, errors);
    }

    if (ctx.confirm && ctx.allocationNumber === 1) {
      this.validateTaxAuthority(ctx, errors);
    }

    return errors;
  }

  getMessage(error: InvoiceFieldError, lang: string): string {
    const l = (lang === 'ar' || lang === 'he' ? lang : 'en') as Lang;
    return l === 'ar' ? error.messageAr : l === 'he' ? error.messageHe : error.messageEn;
  }

  /** Parse backend errors[] into structured errors for scroll/highlight (fallback). */
  parseBackendResponse(
    response: MultiLangMessagePayload | any,
    correlationId: string,
  ): InvoiceFieldError[] {
    const errors: InvoiceFieldError[] = [];
    const statusCode = response?.statusCode;

    if (Array.isArray(response?.errors)) {
      for (const raw of response.errors) {
        const parsed = this.parseBackendErrorLine(String(raw), correlationId, statusCode);
        if (parsed) {
          errors.push(parsed);
        }
      }
    }

    if (errors.length === 0 && response && response.success === false) {
      errors.push({
        errorCode: `BE-${statusCode ?? 'UNKNOWN'}`,
        source: 'backend',
        section: 'business',
        fieldPath: 'header',
        messageAr: response.messageAr || response.message || 'خطأ من الخادم',
        messageEn: response.messageEn || response.message || 'Server error',
        messageHe: response.messageHe || response.message || 'שגיאת שרת',
        correlationId,
      });
    }

    return errors;
  }

  private parseBackendErrorLine(
    line: string,
    correlationId: string,
    statusCode?: number,
  ): InvoiceFieldError | null {
    const itemMatch = line.match(/^العنصر\s+(\d+):\s*(.+)$/);
    if (itemMatch) {
      const row = +itemMatch[1];
      const detail = itemMatch[2];
      const field = this.mapArabicItemDetailToField(detail);
      return this.makeError({
        errorCode: `BE-${statusCode ?? 2001}-ITEM-${field.toUpperCase()}`,
        source: 'backend',
        section: 'item',
        fieldPath: `items.${row - 1}.${field}`,
        row,
        correlationId,
        ...this.itemDetailMessages(row, field, detail),
      });
    }

    const payMatch = line.match(/^طريقة الدفع\s+(\d+):\s*(.+)$/);
    if (payMatch) {
      const paymentIndex = +payMatch[1];
      const detail = payMatch[2];
      const field = this.mapArabicPaymentDetailToField(detail);
      const fieldPath =
        field === 'amount' || field === 'type'
          ? `payment_methods.${paymentIndex - 1}.${field}`
          : `payment_methods.${paymentIndex - 1}.details.${field}`;
      return this.makeError({
        errorCode: `BE-${statusCode ?? 2002}-PAY-${field.toUpperCase()}`,
        source: 'backend',
        section: 'payment',
        fieldPath,
        paymentIndex,
        correlationId,
        ...this.paymentDetailMessages(paymentIndex, field, detail),
      });
    }

    if (line.includes('عنصر واحد على الأقل')) {
      return this.makeError({
        errorCode: `BE-${statusCode ?? 2001}-ITEMS-EMPTY`,
        source: 'backend',
        section: 'item',
        fieldPath: 'items',
        correlationId,
        messageAr: line,
        messageEn: 'At least one item is required',
        messageHe: 'נדרש לפחות פריט אחד',
      });
    }

    if (line.includes('طريقة دفع واحدة على الأقل')) {
      return this.makeError({
        errorCode: `BE-${statusCode ?? 2002}-PAY-EMPTY`,
        source: 'backend',
        section: 'payment',
        fieldPath: 'payment_methods',
        correlationId,
        messageAr: line,
        messageEn: 'At least one payment method is required',
        messageHe: 'נדרש לפחות אמצעי תשלום אחד',
      });
    }

    return this.makeError({
      errorCode: `BE-${statusCode ?? 2000}-GENERIC`,
      source: 'backend',
      section: 'business',
      fieldPath: 'header',
      correlationId,
      messageAr: line,
      messageEn: line,
      messageHe: line,
    });
  }

  private mapArabicItemDetailToField(detail: string): string {
    if (detail.includes('اسم العنصر')) return 'item_name';
    if (detail.includes('معرف المنتج')) return 'product_id';
    if (detail.includes('سعر الوحدة')) return 'price_per_unit';
    if (detail.includes('الكمية')) return 'quantity';
    return 'item_name';
  }

  private mapArabicPaymentDetailToField(detail: string): string {
    if (detail.includes('المبلغ')) return 'amount';
    if (detail.includes('نوع الدفع')) return 'type';
    return 'amount';
  }

  private validateHeader(
    v: Record<string, any>,
    ctx: InvoiceValidationContext,
    errors: InvoiceFieldError[],
  ): void {
    if (!this.isValidCompanyId(v.company_id)) {
      errors.push(
        this.headerError('FE-INV-HDR-001', 'company_id', {
          ar: 'المصلحة التجارية مطلوبة',
          en: 'Company is required',
          he: 'חברה נדרשת',
        }, ctx.correlationId),
      );
    }

    if (v.customer_id == null || v.customer_id === '' || !Number.isFinite(Number(v.customer_id))) {
      errors.push(
        this.headerError('FE-INV-HDR-002', 'customer_id', {
          ar: 'الزبون مطلوب',
          en: 'Customer is required',
          he: 'לקוח נדרש',
        }, ctx.correlationId),
      );
    }

    if (v.invoice_type == null || v.invoice_type === '') {
      errors.push(
        this.headerError('FE-INV-HDR-003', 'invoice_type', {
          ar: 'نوع المستند مطلوب',
          en: 'Document type is required',
          he: 'סוג מסמך נדרש',
        }, ctx.correlationId),
      );
    }

    if (!v.invoice_date) {
      errors.push(
        this.headerError('FE-INV-HDR-004', 'invoice_date', {
          ar: 'تاريخ المستند مطلوب',
          en: 'Document date is required',
          he: 'תאריך מסמך נדרש',
        }, ctx.correlationId),
      );
    }

    const lang = v.document_language;
    if (!lang || !['ar', 'he', 'en'].includes(lang)) {
      errors.push(
        this.headerError('FE-INV-HDR-005', 'document_language', {
          ar: 'لغة المستند مطلوبة',
          en: 'Document language is required',
          he: 'שפת מסמך נדרשת',
        }, ctx.correlationId),
      );
    }
  }

  private isForeignDocumentCurrency(v: Record<string, any>): boolean {
    const code = String(v.currency ?? 'ILS').trim().toUpperCase();
    return code !== '' && code !== 'ILS';
  }

  private validateForeignCurrency(
    v: Record<string, any>,
    ctx: InvoiceValidationContext,
    errors: InvoiceFieldError[],
  ): void {
    const rate = Number(v.exchange_rate);
    if (!Number.isFinite(rate) || rate <= 0) {
      errors.push(
        this.makeError({
          errorCode: 'FE-INV-FX-001',
          source: 'frontend',
          section: 'business',
          fieldPath: 'exchange_rate',
          correlationId: ctx.correlationId,
          messageAr: 'سعر الصرف مطلوب للعملة الأجنبية',
          messageEn: 'Exchange rate is required for foreign currency',
          messageHe: 'שער חליפין נדרש למטבע זר',
        }),
      );
    }

    const date = v.exchange_rate_date ?? v.invoice_date;
    if (!date) {
      errors.push(
        this.makeError({
          errorCode: 'FE-INV-FX-002',
          source: 'frontend',
          section: 'business',
          fieldPath: 'exchange_rate_date',
          correlationId: ctx.correlationId,
          messageAr: 'تاريخ سعر الصرف مطلوب للعملة الأجنبية',
          messageEn: 'Exchange rate date is required for foreign currency',
          messageHe: 'תאריך שער חליפין נדרש למטבע זר',
        }),
      );
    }

    if (ctx.hasItems) {
      const items: any[] = Array.isArray(v.items) ? v.items : [];
      items.forEach((item, index) => {
        const priceForeign = Number(item?.price_per_unit_foreign);
        if (!Number.isFinite(priceForeign) || priceForeign < 0) {
          errors.push(
            this.makeError({
              errorCode: 'FE-INV-FX-003',
              source: 'frontend',
              section: 'item',
              fieldPath: `items.${index}.price_per_unit_foreign`,
              row: index + 1,
              correlationId: ctx.correlationId,
              messageAr: `السطر ${index + 1}: السعر بالعملة الأجنبية مطلوب`,
              messageEn: `Row ${index + 1}: Foreign currency unit price is required`,
              messageHe: `שורה ${index + 1}: מחיר ליחידה במטבע זר נדרש`,
            }),
          );
        }
      });
    }
  }

  private validateBusinessRules(
    v: Record<string, any>,
    ctx: InvoiceValidationContext,
    errors: InvoiceFieldError[],
  ): void {
    if (v.company_id != null && v.invoice_type != null && ctx.allowedInvoiceTypes != null) {
      if (!ctx.allowedInvoiceTypes.includes(+v.invoice_type)) {
        errors.push(
          this.makeError({
            errorCode: 'FE-INV-BIZ-001',
            source: 'frontend',
            section: 'business',
            fieldPath: 'invoice_type',
            correlationId: ctx.correlationId,
            messageAr: 'نوع المستند غير مسموح به لنوع المصلحة التجارية المختارة',
            messageEn: 'This document type is not allowed for the selected company type',
            messageHe: 'סוג מסמך זה אינו מותר לסוג החברה שנבחרה',
          }),
        );
      }
    }
  }

  private validateItems(
    v: Record<string, any>,
    ctx: InvoiceValidationContext,
    errors: InvoiceFieldError[],
  ): void {
    const items: any[] = Array.isArray(v.items) ? v.items : [];

    if (items.length === 0) {
      errors.push(
        this.makeError({
          errorCode: 'FE-INV-ITEM-000',
          source: 'frontend',
          section: 'item',
          fieldPath: 'items',
          correlationId: ctx.correlationId,
          messageAr: 'يجب إضافة عنصر واحد على الأقل للفاتورة',
          messageEn: 'At least one item is required',
          messageHe: 'נדרש לפחות פריט אחד',
        }),
      );
      return;
    }

    items.forEach((item, index) => {
      const row = index + 1;
      const isPresenting = !!item?.isPresenting;
      const itemName = String(item?.item_name || item?.description || '').trim();
      const pid = item?.product_id;
      const hasProduct = pid != null && pid !== '' && +pid !== 0;

      if (!isPresenting) {
        // Empty line: missing name and product are the same issue — report once on product_id.
        if (!itemName && !hasProduct) {
          errors.push(
            this.itemError('FE-INV-ITEM-005', index, 'product_id', {
              ar: `السطر ${row}: المنتج مطلوب`,
              en: `Row ${row}: Product is required`,
              he: `שורה ${row}: מוצר נדרש`,
            }, ctx.correlationId),
          );
        } else {
          if (!itemName) {
            errors.push(
              this.itemError('FE-INV-ITEM-001', index, 'item_name', {
                ar: `السطر ${row}: اسم المنتج مطلوب`,
                en: `Row ${row}: Product name is required`,
                he: `שורה ${row}: שם המוצר נדרש`,
              }, ctx.correlationId),
            );
          } else if (itemName.length > 300) {
            errors.push(
              this.itemError('FE-INV-ITEM-007', index, 'item_name', {
                ar: `السطر ${row}: اسم المنتج يتجاوز 300 حرف`,
                en: `Row ${row}: Product name exceeds 300 characters`,
                he: `שורה ${row}: שם המוצר חורג מ-300 תווים`,
              }, ctx.correlationId),
            );
          }

          if (!hasProduct) {
            errors.push(
              this.itemError('FE-INV-ITEM-005', index, 'product_id', {
                ar: `السطر ${row}: المنتج مطلوب`,
                en: `Row ${row}: Product is required`,
                he: `שורה ${row}: מוצר נדרש`,
              }, ctx.correlationId),
            );
          }
        }
      } else if (!itemName) {
        errors.push(
          this.itemError('FE-INV-ITEM-001', index, 'item_name', {
            ar: `السطر ${row}: اسم المنتج مطلوب`,
            en: `Row ${row}: Product name is required`,
            he: `שורה ${row}: שם המוצר נדרש`,
          }, ctx.correlationId),
        );
      } else if (itemName.length > 300) {
        errors.push(
          this.itemError('FE-INV-ITEM-007', index, 'item_name', {
            ar: `السطر ${row}: اسم المنتج يتجاوز 300 حرف`,
            en: `Row ${row}: Product name exceeds 300 characters`,
            he: `שורה ${row}: שם המוצר חורג מ-300 תווים`,
          }, ctx.correlationId),
        );
      }

      const qty = Number(item?.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        errors.push(
          this.itemError('FE-INV-ITEM-002', index, 'quantity', {
            ar: `السطر ${row}: الكمية يجب أن تكون أكبر من صفر`,
            en: `Row ${row}: Quantity must be greater than zero`,
            he: `שורה ${row}: הכמות חייבת להיות גדולה מאפס`,
          }, ctx.correlationId),
        );
      }

      const price = item?.price_per_unit;
      if (price === null || price === undefined || price === '' || !Number.isFinite(Number(price))) {
        errors.push(
          this.itemError('FE-INV-ITEM-003', index, 'price_per_unit', {
            ar: `السطر ${row}: السعر غير صالح`,
            en: `Row ${row}: Price is invalid`,
            he: `שורה ${row}: המחיר אינו תקין`,
          }, ctx.correlationId),
        );
      }

      const numericFields: Array<{ key: string; code: string; label: { ar: string; en: string; he: string } }> = [
        {
          key: 'sub_total',
          code: 'FE-INV-ITEM-006',
          label: {
            ar: `السطر ${row}: المجموع الفرعي غير صالح`,
            en: `Row ${row}: Subtotal is invalid`,
            he: `שורה ${row}: סכום ביניים אינו תקין`,
          },
        },
        {
          key: 'discount',
          code: 'FE-INV-ITEM-008',
          label: {
            ar: `السطر ${row}: الخصم غير صالح`,
            en: `Row ${row}: Discount is invalid`,
            he: `שורה ${row}: הנחה אינה תקינה`,
          },
        },
        {
          key: 'total_amount',
          code: 'FE-INV-ITEM-009',
          label: {
            ar: `السطر ${row}: المبلغ الإجمالي غير صالح`,
            en: `Row ${row}: Total amount is invalid`,
            he: `שורה ${row}: סכום כולל אינו תקין`,
          },
        },
        {
          key: 'vat_rate',
          code: 'FE-INV-ITEM-010',
          label: {
            ar: `السطر ${row}: نسبة الضريبة غير صالحة`,
            en: `Row ${row}: VAT rate is invalid`,
            he: `שורה ${row}: שיעור מע"מ אינו תקין`,
          },
        },
        {
          key: 'vat_amount',
          code: 'FE-INV-ITEM-011',
          label: {
            ar: `السطر ${row}: مبلغ الضريبة غير صالح`,
            en: `Row ${row}: VAT amount is invalid`,
            he: `שורה ${row}: סכום מע"מ אינו תקין`,
          },
        },
      ];

      for (const nf of numericFields) {
        const val = item?.[nf.key];
        if (val === null || val === undefined || val === '' || !Number.isFinite(Number(val))) {
          errors.push(this.itemError(nf.code, index, nf.key, nf.label, ctx.correlationId));
        }
      }

      const catalogId = item?.catalog_id;
      if (catalogId != null && String(catalogId).length > 13) {
        errors.push(
          this.itemError('FE-INV-ITEM-012', index, 'catalog_id', {
            ar: `السطر ${row}: رقم الكتالوج يتجاوز 13 حرف`,
            en: `Row ${row}: Catalog ID exceeds 13 characters`,
            he: `שורה ${row}: מזהה קטלוג חורג מ-13 תווים`,
          }, ctx.correlationId),
        );
      }

      const desc = item?.description;
      if (desc != null && String(desc).length > 300) {
        errors.push(
          this.itemError('FE-INV-ITEM-013', index, 'description', {
            ar: `السطر ${row}: الوصف يتجاوز 300 حرف`,
            en: `Row ${row}: Description exceeds 300 characters`,
            he: `שורה ${row}: תיאור חורג מ-300 תווים`,
          }, ctx.correlationId),
        );
      }
    });
  }

  private validatePayments(
    v: Record<string, any>,
    ctx: InvoiceValidationContext,
    errors: InvoiceFieldError[],
  ): void {
    const payments: any[] = Array.isArray(v.payment_methods) ? v.payment_methods : [];

    if (payments.length === 0) {
      errors.push(
        this.makeError({
          errorCode: 'FE-INV-PAY-000',
          source: 'frontend',
          section: 'payment',
          fieldPath: 'payment_methods',
          correlationId: ctx.correlationId,
          messageAr: 'يجب إضافة طريقة دفع واحدة على الأقل للفاتورة',
          messageEn: 'At least one payment method is required',
          messageHe: 'נדרש לפחות אמצעי תשלום אחד',
        }),
      );
      return;
    }

    payments.forEach((pm, index) => {
      const payNum = index + 1;
      const type = +pm?.type;
      const paymentMethod = ctx.paymentMethodsList.find(
        (m) => +m.payment_method_code === type,
      );

      if (!paymentMethod || paymentMethod.status !== 1) {
        errors.push(
          this.paymentError('FE-INV-PAY-001', index, 'type', {
            ar: `طريقة الدفع ${payNum}: نوع الدفع غير صحيح`,
            en: `Payment ${payNum}: Invalid payment type`,
            he: `תשלום ${payNum}: סוג תשלום לא תקין`,
          }, ctx.correlationId),
        );
      }

      const amount = Number(pm?.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        errors.push(
          this.paymentError('FE-INV-PAY-002', index, 'amount', {
            ar: `طريقة الدفع ${payNum}: المبلغ يجب أن يكون أكبر من صفر`,
            en: `Payment ${payNum}: Amount must be greater than zero`,
            he: `תשלום ${payNum}: הסכום חייב להיות גדול מאפס`,
          }, ctx.correlationId),
        );
      }

      const details = pm?.details || {};
      const requiredFields = paymentMethod?.required_fields || [];
      const optionalBankFields = new Set(['bank', 'branch', 'account']);

      if (!details.date) {
        errors.push(
          this.paymentDetailError('FE-INV-PAY-003', index, 'date', {
            ar: `طريقة الدفع ${payNum}: تاريخ الدفع مطلوب`,
            en: `Payment ${payNum}: Payment date is required`,
            he: `תשלום ${payNum}: תאריך תשלום נדרש`,
          }, ctx.correlationId),
        );
      }

      for (const field of requiredFields) {
        if (optionalBankFields.has(field)) {
          continue;
        }
        const val = details[field];
        if (val == null || String(val).trim() === '') {
          const labels = this.paymentDetailLabels[field] || {
            ar: field,
            en: field,
            he: field,
          };
          errors.push(
            this.paymentDetailError('FE-INV-PAY-010', index, field, {
              ar: `طريقة الدفع ${payNum}: ${labels.ar} مطلوب`,
              en: `Payment ${payNum}: ${labels.en} is required`,
              he: `תשלום ${payNum}: ${labels.he} נדרש`,
            }, ctx.correlationId),
          );
        }
      }
    });
  }


  private validateTaxAuthority(ctx: InvoiceValidationContext, errors: InvoiceFieldError[]): void {
    const customer = ctx.selectedCustomer;
    const worker =
      customer?.customer_vat_number ||
      customer?.id_number ||
      customer?.customer_id_number ||
      '';
    if (!String(worker).trim()) {
      errors.push(
        this.makeError({
          errorCode: 'FE-INV-TAX-002',
          source: 'frontend',
          section: 'customer',
          fieldPath: 'customer_id',
          correlationId: ctx.correlationId,
          messageAr: 'بيانات الزبون: الرقم الضريبي أو رقم الهوية مطلوب لإرسال المستند لمصلحة الضرائب',
          messageEn: 'Customer: VAT or ID number is required for tax authority submission',
          messageHe: 'לקוח: מספר עוסק או תעודת זהות נדרש לשליחה לרשות המס',
        }),
      );
    }
  }


  private isValidCompanyId(value: unknown): boolean {
    if (value == null || value === '') {
      return false;
    }
    const n = Number(value);
    return Number.isFinite(n) && n > 0;
  }

  private headerError(
    code: string,
    field: string,
    msg: { ar: string; en: string; he: string },
    correlationId: string,
  ): InvoiceFieldError {
    return this.makeError({
      errorCode: code,
      source: 'frontend',
      section: 'header',
      fieldPath: field,
      correlationId,
      messageAr: msg.ar,
      messageEn: msg.en,
      messageHe: msg.he,
    });
  }

  private itemError(
    code: string,
    index: number,
    field: string,
    msg: { ar: string; en: string; he: string },
    correlationId: string,
  ): InvoiceFieldError {
    return this.makeError({
      errorCode: code,
      source: 'frontend',
      section: 'item',
      fieldPath: `items.${index}.${field}`,
      row: index + 1,
      correlationId,
      messageAr: msg.ar,
      messageEn: msg.en,
      messageHe: msg.he,
    });
  }

  private paymentError(
    code: string,
    index: number,
    field: string,
    msg: { ar: string; en: string; he: string },
    correlationId: string,
  ): InvoiceFieldError {
    return this.makeError({
      errorCode: code,
      source: 'frontend',
      section: 'payment',
      fieldPath: `payment_methods.${index}.${field}`,
      paymentIndex: index + 1,
      correlationId,
      messageAr: msg.ar,
      messageEn: msg.en,
      messageHe: msg.he,
    });
  }

  private paymentDetailError(
    code: string,
    index: number,
    field: string,
    msg: { ar: string; en: string; he: string },
    correlationId: string,
  ): InvoiceFieldError {
    return this.makeError({
      errorCode: code,
      source: 'frontend',
      section: 'payment',
      fieldPath: `payment_methods.${index}.details.${field}`,
      paymentIndex: index + 1,
      correlationId,
      messageAr: msg.ar,
      messageEn: msg.en,
      messageHe: msg.he,
    });
  }

  private itemDetailMessages(
    row: number,
    field: string,
    arabicDetail: string,
  ): { messageAr: string; messageEn: string; messageHe: string } {
    const map: Record<string, { en: string; he: string }> = {
      item_name: {
        en: `Row ${row}: Product name is required`,
        he: `שורה ${row}: שם המוצר נדרש`,
      },
      product_id: {
        en: `Row ${row}: Product is required`,
        he: `שורה ${row}: מוצר נדרש`,
      },
      quantity: {
        en: `Row ${row}: Quantity must be greater than zero`,
        he: `שורה ${row}: הכמות חייבת להיות גדולה מאפס`,
      },
      price_per_unit: {
        en: `Row ${row}: Unit price is required`,
        he: `שורה ${row}: מחיר ליחידה נדרש`,
      },
    };
    const m = map[field];
    return {
      messageAr: `السطر ${row}: ${arabicDetail}`,
      messageEn: m?.en || `Row ${row}: ${arabicDetail}`,
      messageHe: m?.he || `שורה ${row}: ${arabicDetail}`,
    };
  }

  private paymentDetailMessages(
    paymentIndex: number,
    field: string,
    arabicDetail: string,
  ): { messageAr: string; messageEn: string; messageHe: string } {
    const map: Record<string, { en: string; he: string }> = {
      amount: {
        en: `Payment ${paymentIndex}: Amount must be greater than zero`,
        he: `תשלום ${paymentIndex}: הסכום חייב להיות גדול מאפס`,
      },
      type: {
        en: `Payment ${paymentIndex}: Invalid payment type`,
        he: `תשלום ${paymentIndex}: סוג תשלום לא תקין`,
      },
    };
    const m = map[field];
    return {
      messageAr: `طريقة الدفع ${paymentIndex}: ${arabicDetail}`,
      messageEn: m?.en || `Payment ${paymentIndex}: ${arabicDetail}`,
      messageHe: m?.he || `תשלום ${paymentIndex}: ${arabicDetail}`,
    };
  }

  private makeError(partial: InvoiceFieldError): InvoiceFieldError {
    return partial;
  }
}
